/**
 * Utility for parsing individual cell values from a training matrix spreadsheet
 * into structured data. Handles dates, yes/no values, certificate numbers,
 * status text, and Excel serial dates.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMPLETED_VALUES = new Set([
  'yes', 'y', 'complete', 'completed', 'done', 'pass', 'passed',
  'x', 'xx', '\u2713', '\u2714',
]);

const PENDING_VALUES = new Set([
  'no', 'n', 'incomplete', 'fail', 'failed', '\u2717', '\u2718',
]);

const IN_PROGRESS_VALUES = new Set([
  'booked', 'training booked', 'in progress', 'scheduled', 'pending',
  'to book', 'training to be booked',
]);

const EXPIRED_VALUES = new Set([
  'expired', 'lapsed',
]);

const NOT_REQUIRED_VALUES = new Set([
  'exempt', 'n/a', 'na', 'not applicable', 'not required', '/',
]);

// Matches strings that look like certificate / reference numbers:
// alphanumeric with optional dashes or slashes, at least 2 chars, containing
// at least one letter and at least one digit.
const CERT_NUMBER_RE = /^[A-Za-z0-9][A-Za-z0-9\-/]{1,}$/;

const DATE_SEPARATOR_RE = /[/\-.]/;

// ---------------------------------------------------------------------------
// excelSerialToDate
// ---------------------------------------------------------------------------

/**
 * Converts an Excel serial date number to a JavaScript Date.
 *
 * Excel uses an epoch of January 1, 1900 and includes the well-known
 * Lotus 1-2-3 leap year bug where serial 60 is treated as Feb 29, 1900
 * (a date that does not exist). Serials > 60 are therefore off by one day
 * relative to a naive calculation.
 *
 * @param {number} serial - The Excel serial date number.
 * @returns {Date|null} A Date object, or null if the serial is invalid.
 */
export function excelSerialToDate(serial) {
  if (typeof serial !== 'number' || !isFinite(serial) || serial < 1) {
    return null;
  }

  let adjustedSerial = serial;
  if (serial > 60) {
    adjustedSerial = serial - 1;
  } else if (serial === 60) {
    adjustedSerial = 59;
  }

  const epoch = new Date(1900, 0, 1);
  epoch.setDate(epoch.getDate() + (adjustedSerial - 1));
  return epoch;
}

// ---------------------------------------------------------------------------
// parseDateString
// ---------------------------------------------------------------------------

/**
 * Parses a date string according to the given format.
 *
 * Supported formats: 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'.
 * Separators '/', '-', and '.' are accepted regardless of the format string.
 * Two-digit years are interpreted as 20xx when < 30, and 19xx when >= 30.
 *
 * @param {string} str - The date string to parse.
 * @param {string} [format='DD/MM/YYYY'] - The expected date format.
 * @returns {Date|null} A Date object, or null if the string cannot be parsed.
 */
export function parseDateString(str, format = 'DD/MM/YYYY') {
  if (!str || typeof str !== 'string') return null;

  const trimmed = str.trim();
  if (!trimmed) return null;

  const parts = trimmed.split(DATE_SEPARATOR_RE);
  if (parts.length !== 3) return null;

  let day, month, year;

  switch (format) {
    case 'YYYY-MM-DD': {
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
      break;
    }
    case 'MM/DD/YYYY': {
      month = parseInt(parts[0], 10);
      day = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
      break;
    }
    case 'DD/MM/YYYY':
    default: {
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
      break;
    }
  }

  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

  // Handle 2-digit years
  if (year < 100) {
    year = year < 30 ? 2000 + year : 1900 + year;
  }

  // Basic validity checks
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;

  const date = new Date(year, month - 1, day);

  // Verify the date didn't roll over (e.g. Feb 30 → Mar 2)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

// ---------------------------------------------------------------------------
// formatDateForDB
// ---------------------------------------------------------------------------

/**
 * Formats a Date as a 'YYYY-MM-DD' string suitable for Supabase date columns.
 *
 * @param {Date} date - The Date to format.
 * @returns {string|null} The formatted date string, or null if the input is invalid.
 */
export function formatDateForDB(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ---------------------------------------------------------------------------
// classifyDate
// ---------------------------------------------------------------------------

/**
 * Determines whether a parsed date is a completion date or expiry date.
 *
 * When dateInterpretation is 'auto', dates in the future are treated as
 * expiry dates and dates in the past as completion dates.
 *
 * @param {Date} date
 * @param {'completion'|'expiry'|'auto'} interpretation
 * @returns {{ completedDate: string|null, expiryDate: string|null }}
 */
function classifyDate(date, interpretation) {
  const dbDate = formatDateForDB(date);
  if (!dbDate) return { completedDate: null, expiryDate: null };

  if (interpretation === 'auto') {
    const now = new Date();
    if (date > now) {
      return { completedDate: null, expiryDate: dbDate };
    }
    return { completedDate: dbDate, expiryDate: null };
  }

  if (interpretation === 'expiry') {
    return { completedDate: null, expiryDate: dbDate };
  }

  return { completedDate: dbDate, expiryDate: null };
}

// ---------------------------------------------------------------------------
// parseCellValue
// ---------------------------------------------------------------------------

/**
 * Parses a raw cell value from a training matrix spreadsheet into structured data.
 *
 * @param {*} rawValue - The raw cell value (string, number, boolean, null, etc.)
 * @param {Object} [options={}]
 * @param {'auto'|'DD/MM/YYYY'|'MM/DD/YYYY'|'YYYY-MM-DD'} [options.dateFormat='DD/MM/YYYY']
 * @param {'completion'|'expiry'|'auto'} [options.dateInterpretation='auto']
 * @returns {{ status: string, completedDate: string|null, expiryDate: string|null,
 *             certificateNumber: string|null, rawValue: *, parsed: boolean,
 *             skip: boolean }}
 */
export function parseCellValue(rawValue, options = {}) {
  const {
    dateFormat = 'DD/MM/YYYY',
    dateInterpretation = 'auto',
  } = options;

  const base = {
    completedDate: null,
    expiryDate: null,
    certificateNumber: null,
    rawValue,
    skip: false,
  };

  // --- Empty / null / undefined → skip, don't create an assignment ---
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return { ...base, status: 'pending', parsed: true, skip: true };
  }

  // --- Boolean ---
  if (rawValue === true) {
    return { ...base, status: 'completed', parsed: true };
  }
  if (rawValue === false) {
    return { ...base, status: 'pending', parsed: true, skip: true };
  }

  // --- Numeric: possibly an Excel serial date ---
  if (typeof rawValue === 'number') {
    // Reasonable serial date range: ~1900 to ~2200
    if (rawValue >= 1 && rawValue <= 110000) {
      const date = excelSerialToDate(rawValue);
      if (date) {
        const dates = classifyDate(date, dateInterpretation);
        return {
          ...base,
          status: 'completed',
          ...dates,
          parsed: true,
        };
      }
    }
    // Numbers outside the serial range → unparseable
    return { ...base, status: 'pending', parsed: false };
  }

  // From here on we work with trimmed string values
  const strValue = String(rawValue).trim();
  if (strValue === '') {
    return { ...base, status: 'pending', parsed: true, skip: true };
  }

  const lower = strValue.toLowerCase();

  // --- Status keywords ---
  if (COMPLETED_VALUES.has(lower)) {
    return { ...base, status: 'completed', parsed: true };
  }
  if (PENDING_VALUES.has(lower)) {
    return { ...base, status: 'pending', parsed: true, skip: true };
  }
  if (IN_PROGRESS_VALUES.has(lower)) {
    return { ...base, status: 'in_progress', parsed: true };
  }
  if (EXPIRED_VALUES.has(lower)) {
    return { ...base, status: 'expired', parsed: true };
  }
  if (NOT_REQUIRED_VALUES.has(lower)) {
    return { ...base, status: 'not_required', parsed: true, skip: true };
  }

  // --- Date string ---
  const parsedDate = attemptDateParse(strValue, dateFormat);
  if (parsedDate) {
    const dates = classifyDate(parsedDate, dateInterpretation);
    return {
      ...base,
      status: 'completed',
      ...dates,
      parsed: true,
    };
  }

  // --- Certificate / reference number ---
  if (looksLikeCertificateNumber(strValue)) {
    return {
      ...base,
      status: 'completed',
      certificateNumber: strValue,
      parsed: true,
    };
  }

  // --- Fallback: unrecognised value ---
  return { ...base, status: 'pending', parsed: false };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Attempts to parse a string as a date, trying the specified format first.
 * When format is 'auto', tries DD/MM/YYYY, MM/DD/YYYY, and YYYY-MM-DD in order.
 */
function attemptDateParse(str, format) {
  if (format === 'auto') {
    return (
      parseDateString(str, 'DD/MM/YYYY') ||
      parseDateString(str, 'MM/DD/YYYY') ||
      parseDateString(str, 'YYYY-MM-DD')
    );
  }
  return parseDateString(str, format);
}

/**
 * Determines whether a string looks like a certificate or reference number.
 * Must contain at least one letter and at least one digit, be 2+ chars,
 * and consist only of alphanumeric characters, dashes, and slashes.
 */
function looksLikeCertificateNumber(str) {
  if (!CERT_NUMBER_RE.test(str)) return false;
  const hasLetter = /[A-Za-z]/.test(str);
  const hasDigit = /\d/.test(str);
  return hasLetter && hasDigit;
}
